import {
  ASSET_DOCUMENT_KIND_LABELS,
  ASSET_KIND_LABELS,
  ASSET_KINDS,
  type AssetKind,
  type MaintenanceAssetDetail,
  USAGE_SOURCE_LABELS,
} from "@station/contracts";
import { toIstanbulDate } from "@station/core";
import { Alert, Button, Card, EmptyState, SkeletonRows, StatusBadge, useToast } from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Archive, SearchX } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AssetDocumentDialog,
  MeterReadingDialog,
} from "../../../../../features/maintenance/asset-dialogs.js";
import {
  formatDecimal,
  planDueText,
  planIntervalText,
  warrantyState,
} from "../../../../../features/maintenance/maintenance-values.js";
import { PlanDialog } from "../../../../../features/maintenance/plan-dialog.js";
import {
  useMaintenanceAsset,
  useMaintenancePermissions,
  useRefreshMaintenance,
} from "../../../../../features/maintenance/queries.js";
import {
  DetailRow,
  FileOpenButton,
  MaintenancePage,
  PriorityBadge,
  SlaBadge,
  WorkOrderStatusBadge,
} from "../../../../../features/maintenance/shared.js";
import { NewWorkOrderDialog } from "../../../../../features/maintenance/work-order-dialogs.js";
import { api } from "../../../../../lib/api.js";
import { errorMessage, isApiError } from "../../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../../lib/format.js";

export const Route = createFileRoute("/_app/bakim/varliklar/$kind/$id")({ component: AssetRoute });

/**
 * Varlık kartı (Prompt 23 web, `GET /maintenance/assets/:kind/:id`): yaşam döngüsü alanları (seri no, kurulum, garanti,
 * servis firması — düzenleme istasyon varlık ekranından), belgeler (ekle / arşivle), bakım planları, son iş emirleri, elle
 * sayaç okumaları ve son 30 gün kullanımı.
 */
function AssetRoute(): ReactElement {
  const { t } = useTranslation();
  const params = Route.useParams();
  const kind: AssetKind = (ASSET_KINDS as readonly string[]).includes(params.kind)
    ? (params.kind as AssetKind)
    : "DEVICE";
  const id = params.id;
  const permissions = useMaintenancePermissions();
  const asset = useMaintenanceAsset(kind, id, permissions.enabled && permissions.canRead);
  const [dialog, setDialog] = useState<"reading" | "document" | "plan" | "workOrder" | null>(null);
  const data = asset.data;
  const title = data?.asset.name ?? t("maintenance.assets.detailTitle");
  return (
    <MaintenancePage
      title={title}
      {...(data
        ? {
            description: `${ASSET_KIND_LABELS[data.asset.assetKind]} · ${data.asset.stationCode} ${data.asset.stationName}`,
          }
        : {})}
      breadcrumb={[
        {
          label: t("menu.maintenanceAssets"),
          link: (children, className) => (
            <Link to="/bakim/varliklar" className={className}>
              {children}
            </Link>
          ),
        },
        { label: title },
      ]}
      actions={
        data ? (
          <div className="flex flex-wrap gap-2">
            {permissions.canWork ? (
              <Button onClick={() => setDialog("workOrder")}>{t("maintenance.workOrders.new")}</Button>
            ) : null}
            {data.canManagePlans ? (
              <Button variant="outline" onClick={() => setDialog("plan")}>
                {t("maintenance.plans.new")}
              </Button>
            ) : null}
            {data.canManagePlans ? (
              <Button variant="outline" onClick={() => setDialog("reading")}>
                {t("maintenance.assets.addReading")}
              </Button>
            ) : null}
            {data.canManage && permissions.canUpload ? (
              <Button variant="outline" onClick={() => setDialog("document")}>
                {t("maintenance.assets.addDocument")}
              </Button>
            ) : null}
          </div>
        ) : null
      }
    >
      {asset.error ? (
        isApiError(asset.error, "NOT_FOUND") ? (
          <EmptyState
            headingLevel={2}
            icon={<SearchX />}
            title={t("maintenance.assets.notFoundTitle")}
            description={t("maintenance.assets.notFoundDescription")}
          />
        ) : (
          <Alert tone="danger">{errorMessage(asset.error)}</Alert>
        )
      ) : !data ? (
        <SkeletonRows rows={8} />
      ) : (
        <AssetBody detail={data} />
      )}
      {data && dialog === "reading" ? (
        <MeterReadingDialog open onOpenChange={() => setDialog(null)} kind={kind} id={id} />
      ) : null}
      {data && dialog === "document" ? (
        <AssetDocumentDialog open onOpenChange={() => setDialog(null)} kind={kind} id={id} />
      ) : null}
      {data && dialog === "plan" ? (
        <PlanDialog
          open
          onOpenChange={() => setDialog(null)}
          plan={null}
          defaultStationId={data.asset.stationId}
          defaultAsset={{ kind, id }}
        />
      ) : null}
      {data && dialog === "workOrder" ? (
        <NewWorkOrderDialog
          open
          onOpenChange={() => setDialog(null)}
          defaultStationId={data.asset.stationId}
          defaultAsset={{ kind, id }}
        />
      ) : null}
    </MaintenancePage>
  );
}

function AssetBody({ detail }: { detail: MaintenanceAssetDetail }): ReactElement {
  const { t } = useTranslation();
  const asset = detail.asset;
  const warranty = warrantyState(asset.warrantyEndsOn, toIstanbulDate(new Date()));
  return (
    <>
      <Card className="grid gap-4 p-4">
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailRow label={t("maintenance.fields.serialNo")}>{asset.serialNo ?? "—"}</DetailRow>
          <DetailRow label={t("maintenance.fields.brandModel")}>
            {[detail.brand, detail.model].filter(Boolean).join(" · ") || "—"}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.installedOn")}>{formatDate(asset.installedOn)}</DetailRow>
          <DetailRow label={t("maintenance.fields.warrantyEndsOn")}>
            <span className="flex flex-wrap items-center gap-2">
              {formatDate(asset.warrantyEndsOn)}
              {warranty ? <StatusBadge tone={warranty.tone}>{warranty.label}</StatusBadge> : null}
            </span>
          </DetailRow>
          <DetailRow label={t("maintenance.fields.serviceCounterparty")}>
            {asset.serviceCounterparty?.name ?? "—"}
          </DetailRow>
          <DetailRow label={t("maintenance.fields.status")}>
            {asset.status === "ACTIVE" ? t("common.active") : asset.status}
          </DetailRow>
          {detail.usage.map((item) => (
            <DetailRow
              key={item.source}
              label={t("maintenance.assets.usage30", { source: USAGE_SOURCE_LABELS[item.source] })}
            >
              <span className="tabular">{formatDecimal(item.last30Days)}</span>
            </DetailRow>
          ))}
        </dl>
        <p className="text-xs text-muted-foreground">{t("maintenance.assets.editHint")}</p>
      </Card>
      <Documents detail={detail} />
      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("menu.maintenancePlans")}</h2>
        {detail.plans.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("maintenance.assets.noPlans")}</p>
        ) : (
          <ul className="grid gap-2">
            {detail.plans.map((plan) => (
              <li
                key={plan.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
              >
                <span className="grid gap-0.5">
                  <span className="font-medium">{plan.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {planIntervalText(plan)} · {planDueText(plan, formatDate)}
                  </span>
                </span>
                {plan.due.due ? (
                  <StatusBadge tone="warning">{t("maintenance.plans.dueNow")}</StatusBadge>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("maintenance.assets.recentWorkOrders")}</h2>
        {detail.recentWorkOrders.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("maintenance.assets.noWorkOrders")}</p>
        ) : (
          <ul className="grid gap-2">
            {detail.recentWorkOrders.map((order) => (
              <li
                key={order.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
              >
                <span className="grid gap-0.5">
                  <Link
                    to="/bakim/is-emirleri/$id"
                    params={{ id: order.id }}
                    className="font-medium underline underline-offset-4"
                  >
                    {order.title}
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    {order.workOrderNo} · {formatDateTime(order.createdAt)}
                  </span>
                </span>
                <span className="flex flex-wrap gap-1.5">
                  <PriorityBadge priority={order.priority} />
                  <WorkOrderStatusBadge status={order.status} />
                  <SlaBadge sla={order.sla} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("maintenance.assets.readings")}</h2>
        {detail.meterReadings.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("maintenance.assets.noReadings")}</p>
        ) : (
          <ul className="grid gap-1 text-sm">
            {detail.meterReadings.map((reading) => (
              <li
                key={reading.id}
                className="flex flex-wrap justify-between gap-2 border-b py-1.5 last:border-0"
              >
                <span className="tabular">
                  {formatDecimal(reading.value)}{" "}
                  {reading.meter === "HOURS"
                    ? t("maintenance.assets.hoursUnit")
                    : t("maintenance.assets.countUnit")}
                  {reading.isReset ? ` · ${t("maintenance.assets.resetMark")}` : ""}
                </span>
                <span className="text-muted-foreground">
                  {formatDateTime(reading.readAt)}
                  {reading.recordedByName ? ` · ${reading.recordedByName}` : ""}
                  {reading.note ? ` · ${reading.note}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function Documents({ detail }: { detail: MaintenanceAssetDetail }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMaintenance();
  const archive = useMutation({
    mutationFn: async (document: { id: string; version: number }) =>
      (
        await api.POST("/asset-documents/{documentId}/archive", {
          params: { path: { documentId: document.id } },
          body: { version: document.version },
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("maintenance.assets.documentArchived") });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });
  return (
    <Card className="grid gap-3 p-4">
      <h2 className="text-base font-semibold">{t("maintenance.fields.documents")}</h2>
      {detail.documents.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("maintenance.assets.noDocuments")}</p>
      ) : (
        <ul className="grid gap-2">
          {detail.documents.map((document) => (
            <li
              key={document.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
            >
              <span className="grid gap-0.5">
                <span className="font-medium">{document.title}</span>
                <span className="text-xs text-muted-foreground">
                  {ASSET_DOCUMENT_KIND_LABELS[document.docKind]} · {document.fileName} ·{" "}
                  {formatDate(document.createdAt.slice(0, 10))}
                </span>
              </span>
              <span className="flex gap-2">
                <FileOpenButton fileId={document.fileId} label={t("maintenance.assets.openFile")} />
                {detail.canManage ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={archive.isPending && archive.variables?.id === document.id}
                    onClick={() => {
                      if (window.confirm(t("maintenance.assets.archiveConfirm", { title: document.title })))
                        archive.mutate({ id: document.id, version: document.version });
                    }}
                  >
                    <Archive aria-hidden="true" />
                    {t("maintenance.assets.archive")}
                  </Button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
